import React, { FC, useState } from 'react';
import { Wrapper } from 'components/theme/Wrapper';
import {
  ResourceHeading2,
  ResourceParagraph,
} from 'components/theme/Typography/ResourceTypography';
import { NavigationHeader } from 'components/NavigationHeader';
import { useTranslation } from 'react-i18next';
import {
  Header,
  LayoutWrapper,
  Main,
} from 'features/GuestReschedule/components/Layout';
import { PrimaryButton } from 'components/theme/Button';

import { Sep } from 'components/common/styled/common-styled';
import { useHistory } from 'react-router-dom';
import ReservationItem from 'features/GuestReschedule/components/ReservationItem';
import RescheduleNotAllowed from 'features/GuestReschedule/components/RescheduleNotAllowed';
import useGuestReschedule from 'features/GuestReschedule/hooks/useGuestReschedule';
import useInitGuestReschedule from 'features/GuestReschedule/hooks/useInitGuestReschedule';
import NoBookingsAvailable from 'features/GuestReschedule/components/NoBookingsAvailable';
import { useGuestRescheduleSessions } from 'features/GuestReschedule/hooks/useGuestRescheduleSessions';
import { TimeSelector } from 'features/GuestReschedule/components/TimeSelector';
import moment from 'moment';

const GuestRescheduleDatePage: FC = () => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);

  useInitGuestReschedule();

  const {
    canReschedule,
    bookingSelected,
    selectedBookingId,
    cartId,
    token,
    selectedCalendarDate,
    selectedResourceId,
    setSelectedRescheduleDate,
  } = useGuestReschedule();

  const selectedDate = selectedCalendarDate ?? moment().toISOString();
  const selectedMoment = moment(selectedDate);

  const {
    sessions: allSessions,
    experienceId,
    resources,
    isLoading,
  } = useGuestRescheduleSessions(
    bookingSelected?.bookingId,
    selectedMoment.format('YYYY-MM-DD'),
    selectedMoment.clone().add(3, 'months').endOf('month').format('YYYY-MM-DD'),
  );

  const selectedDateStr = selectedMoment.format('YYYY-MM-DD');

  const activeResourceId = selectedResourceId || experienceId;
  const filteredSessions = activeResourceId
    ? allSessions.map((day) => ({
        ...day,
        slots: day.slots.filter(
          (slot) => slot.experienceId === activeResourceId,
        ),
      }))
    : allSessions;

  // The next 3 calendar days with availability (regardless of how many sessions each has)
  const groupedSessions = filteredSessions
    .filter((day) => day.slots.length > 0)
    .slice(0, 3);

  const includesSelected = groupedSessions.some(
    (s) => s.date === selectedDateStr,
  );

  const times = includesSelected
    ? groupedSessions
    : [{ date: selectedDateStr, slots: [] }, ...groupedSessions];

  const handleContinue = () => {
    if (!selectedSlotId) {
      return;
    }

    setSelectedRescheduleDate(selectedSlotId);

    push({
      pathname: `/carts/${cartId}/guest-reschedule/booking/${selectedBookingId}/confirmation`,
      search: `?token=${token}`,
    });
  };

  if (!bookingSelected) {
    return <NoBookingsAvailable title={t('selectADateAndTime')} />;
  }

  if (!canReschedule) {
    return <RescheduleNotAllowed booking={bookingSelected} />;
  }

  const notes = bookingSelected.reschedulePolicyNotes;

  const noSessionsAvailable =
    times.every((day) => day.slots.length === 0) && allSessions.length === 0;

  return (
    <Wrapper>
      <LayoutWrapper>
        <NavigationHeader
          preventDefaultBack
          onGoBack={() =>
            push({
              pathname: `/carts/${cartId}/guest-reschedule`,
              search: `?token=${token}`,
            })
          }
        />
        <Header>
          <ResourceHeading2 fontSize="28px" className="title">
            {t('selectADateAndTime')}
          </ResourceHeading2>
          {notes && (
            <ResourceParagraph
              className="subtitle"
              fontSize="16px"
              opacity="0.7"
              fontWeight="400"
              lineHeight="20px"
            >
              {notes}
            </ResourceParagraph>
          )}
        </Header>
        <Main>
          <ReservationItem booking={bookingSelected} isSelectable={false} />
          <Sep style={{ marginTop: '32px' }} />
          <TimeSelector
            defaultResourceId={experienceId}
            times={times}
            resources={resources}
            noSessionsAvailable={noSessionsAvailable}
            selectedSlotId={selectedSlotId}
            onSelectSlot={setSelectedSlotId}
            isLoading={isLoading}
          />
        </Main>
        {noSessionsAvailable ? null : (
          <PrimaryButton
            big
            disabled={!selectedSlotId}
            onClick={handleContinue}
          >
            {t('continue')}
          </PrimaryButton>
        )}
      </LayoutWrapper>
    </Wrapper>
  );
};

export default GuestRescheduleDatePage;
